import React, { createContext, useContext, useMemo, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';

import { noop } from 'ui-components/default-props/noop';

import { mfaRequiredSubject } from './account-verification-needed.subject';
import { trackSuccess } from './analytics-helpers';
import { showVerifyingInProgressNotification, showVerifyingSuccess, showVerifyingFailed } from './helpers';
import { useAuth0MfaVerificationRequired, useNativeVerificationRequired } from './hooks';
import messages, { messageSets, nativeAuthMessages } from './messages';
import { VerifyEmailModal } from '../../components/verify-email-modal';
import { Auth0Wrapper } from '../../services/auth0/Auth0Wrapper';

import { StepUpAuthContextProvider } from './index';

/**
 * @typedef {import('react-intl').MessageDescriptor} MessageDescriptor
 * @typedef {import('./analytics-helpers').FeatureNames} FeatureNames
 * @typedef {import('./account-verification-needed.subject').MfaRetryRequest} MfaRetryRequest
 */

/**
 * @typedef {object} UpdateMfaClaimContextValue
 * @property {() => void} closeMfaModal
 * @property {(arg: string) => void} openMfaModal
 * @property {() => void} closeNativeVerificationModal
 * @property {(arg: string) => void} openNativeVerificationModal
 */

/**
 * @type {import('react').Context<UpdateMfaClaimContextValue>}
 */
export const UpdateMfaClaimContext = createContext({
  closeMfaModal: noop,
  openMfaModal: noop,
  closeNativeVerificationModal: noop,
  openNativeVerificationModal: noop,
});

export const useUpdateMfaClaimContext = () => useContext(UpdateMfaClaimContext);

/**
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element}
 */
export const UpdateMfaClaimContextProvider = ({ children }) => {
  /** @type {React.MutableRefObject<MfaRetryRequest[]>} */
  const requestQueueRef = useRef([]);
  const isModalOpenRef = useRef(false);

  const retryAllQueuedRequests = useCallback(() => {
    /** @type {MfaRetryRequest[]} */
    const queue = [...requestQueueRef.current];
    requestQueueRef.current = [];

    queue.forEach(async ({ config, resolve, reject }) => {
      try {
        // oxlint-disable-next-line no-param-reassign
        delete config.ss_isMfaRetry;

        // Bypass transformRequest to avoid double-stringification
        // The data is already transformed from the original request
        const retryConfig = {
          ...config,
          transformRequest: [],
        };

        const response = await axios.request(retryConfig);
        resolve(response);
      } catch (error) {
        reject(error);
      }
    });
  }, []);

  const clearRequestQueue = useCallback(() => {
    /** @type {MfaRetryRequest[]} */ const queue = [...requestQueueRef.current];
    requestQueueRef.current = [];

    queue.forEach(({ reject }) => {
      reject(new Error('MFA authentication cancelled by user'));
    });
  }, []);

  const { openMfaModal, closeMfaModal, requestUrl, isMfaModalOpen } = useAuth0MfaVerificationRequired();

  const { nativeVerificationSource, openNativeVerificationModal, closeNativeVerificationModal } = useNativeVerificationRequired();

  const onMfaSuccess = useCallback(async () => {
    const notificationId = showVerifyingInProgressNotification();
    isModalOpenRef.current = false;
    try {
      const shouldSkipCache = true;
      await Auth0Wrapper.getAccessToken(shouldSkipCache);
      showVerifyingSuccess(notificationId);

      retryAllQueuedRequests();
      closeMfaModal();

      trackSuccess({ source: requestUrl });
    } catch {
      showVerifyingFailed(notificationId, onMfaSuccess);
    }
  }, [requestUrl, closeMfaModal, retryAllQueuedRequests]);

  const onMfaModalClose = useCallback(() => {
    closeMfaModal();
    clearRequestQueue();
    isModalOpenRef.current = false;
  }, [closeMfaModal, clearRequestQueue]);

  const onNativeVerificationSuccess = useCallback(() => {
    retryAllQueuedRequests();
  }, [retryAllQueuedRequests]);

  const onNativeModalClose = useCallback(() => {
    closeNativeVerificationModal();
    clearRequestQueue();
    isModalOpenRef.current = false;
  }, [closeNativeVerificationModal, clearRequestQueue]);

  useEffect(() => {
    const subscription = mfaRequiredSubject.subscribe((request) => {
      requestQueueRef.current.push(request);
      if (isModalOpenRef.current) {
        return;
      }
      isModalOpenRef.current = true;

      if (request.isNative) {
        openNativeVerificationModal(request.url);
      } else {
        openMfaModal(request.url);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [openMfaModal, openNativeVerificationModal]);

  useEffect(() => {
    return () => {
      clearRequestQueue();
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(() => {
    return {
      closeMfaModal,
      openMfaModal,
      closeNativeVerificationModal,
      openNativeVerificationModal,
    };
  }, [closeMfaModal, openMfaModal, closeNativeVerificationModal, openNativeVerificationModal]);

  return (
    <UpdateMfaClaimContext.Provider value={value}>
      {isMfaModalOpen ? (
        <StepUpAuthContextProvider
          isModalOpen={isMfaModalOpen}
          messages={messageSets.lastMfaClaimMessages}
          onClose={onMfaModalClose}
          onStepUpAuthSuccess={onMfaSuccess}
          source={requestUrl}
          stepUpAuthModalTitle={messages.mfaModalTitle}
        />
      ) : null}
      {nativeVerificationSource ? (
        <VerifyEmailModal
          buttonText={nativeAuthMessages.primaryAction}
          closeUnverifiedModal={onNativeModalClose}
          onSuccess={onNativeVerificationSuccess}
          source={nativeVerificationSource}
        />
      ) : null}
      {children}
    </UpdateMfaClaimContext.Provider>
  );
};
